/*
 * Postcode-zoeker: vertaalt een Nederlandse postcode naar de dichtstbijzijnde
 * vestiging en stuurt de bezoeker meteen door naar die vestigingspagina.
 *
 * Geocoderen gaat via de PDOK Locatieserver (open data, geen sleutel nodig),
 * dezelfde bron als de winkelzoeker in sample-request.js. Daar moet ook op
 * plaatsnamen gezocht kunnen worden; hier hoeft alleen een postcode herkend te
 * worden, dus dit blijft bewust een compacte eigen variant.
 */

const PDOK_LOCATIE_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free';
const PDOK_FETCH_TIMEOUT_MS = 5000;

// Vestigingspagina waar we op terugvallen als er geen enkele match uitkomt.
const FALLBACK_URL = '/pages/winkels';

const MESSAGES = {
  empty: 'Vul je postcode in om de dichtstbijzijnde megastore te vinden.',
  invalid: 'Dit lijkt geen Nederlandse postcode. Probeer bijvoorbeeld 5611 AB.',
  notFound: 'We konden deze postcode niet vinden. Controleer hem even en probeer het opnieuw.',
};

/*
 * Nederlandse postcode: vier cijfers (niet met 0 beginnend) en optioneel twee
 * letters. De letters mogen ontbreken — vier cijfers zijn ruim genoeg om de
 * juiste vestiging te bepalen.
 */
function parsePostcode(value) {
  const compact = String(value ?? '')
    .toUpperCase()
    .replace(/\s+/g, '');
  const match = compact.match(/^([1-9]\d{3})([A-Z]{2})?$/);
  if (!match) return null;

  return { prefix: match[1], query: match[1] + (match[2] ?? '') };
}

/* PDOK levert de coördinaten als WKT-punt: POINT(lengtegraad breedtegraad). */
function parsePdokCentroid(centroide) {
  const match = /^POINT\(([-\d.]+)\s+([-\d.]+)\)$/i.exec(String(centroide ?? '').trim());
  if (!match) return null;

  const lng = Number.parseFloat(match[1]);
  const lat = Number.parseFloat(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
}

async function geocodePostcode({ prefix, query }) {
  const url = new URL(PDOK_LOCATIE_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('rows', '1');
  url.searchParams.set('fq', 'type:postcode');

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), PDOK_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;

    const data = await response.json();
    const doc = data?.response?.docs?.[0];

    // De vrije zoekopdracht is fuzzy: bij een postcode die niet bestaat komt er
    // een buurpostcode terug. Alleen accepteren wat echt in het ingetypte
    // cijferblok valt, anders sturen we iemand naar de verkeerde kant van het
    // land zonder dat hij het doorheeft.
    const found = String(doc?.postcode ?? '').replace(/\s+/g, '');
    if (!found.startsWith(prefix)) return null;

    return parsePdokCentroid(doc?.centroide_ll);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function haversineDistanceKm(from, to) {
  const earthRadiusKm = 6371;
  const latDelta = ((to.lat - from.lat) * Math.PI) / 180;
  const lngDelta = ((to.lng - from.lng) * Math.PI) / 180;
  const fromLat = (from.lat * Math.PI) / 180;
  const toLat = (to.lat * Math.PI) / 180;

  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findNearestStore(stores, origin) {
  let nearest = null;
  let nearestDistance = Infinity;

  for (const store of stores) {
    const lat = Number.parseFloat(store.latitude);
    const lng = Number.parseFloat(store.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    const distance = haversineDistanceKm(origin, { lat, lng });
    if (distance < nearestDistance) {
      nearest = store;
      nearestDistance = distance;
    }
  }

  return nearest;
}

if (!customElements.get('postcode-vestiging')) {
  class PostcodeVestiging extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-postcode-form]');
      this.input = this.querySelector('[data-postcode-input]');
      this.submit = this.querySelector('[data-postcode-submit]');
      this.message = this.querySelector('[data-postcode-message]');
      if (!this.form || !this.input) return;

      this.stores = this.readStores();
      this.defaultLabel = this.submit?.textContent.trim() ?? '';
      this.busyLabel = this.dataset.busyLabel || 'Zoeken…';

      this.handleSubmit = (event) => this.onSubmit(event);
      this.handleInput = () => this.clearMessage();
      this.form.addEventListener('submit', this.handleSubmit);
      this.input.addEventListener('input', this.handleInput);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.input?.removeEventListener('input', this.handleInput);
    }

    readStores() {
      const dataScript = this.querySelector('script[data-sample-request-store-data]');
      if (!dataScript) return [];

      try {
        return JSON.parse(dataScript.textContent) ?? [];
      } catch {
        return [];
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const raw = this.input.value.trim();
      if (!raw) {
        this.showMessage(MESSAGES.empty);
        return;
      }

      const postcode = parsePostcode(raw);
      if (!postcode) {
        this.showMessage(MESSAGES.invalid);
        return;
      }

      this.setBusy(true);
      const origin = await geocodePostcode(postcode);
      // De bezoeker kan tijdens het wachten al weg zijn geklikt, of de
      // theme-editor heeft het blok opnieuw opgebouwd.
      if (!this.isConnected) return;

      const store = origin ? findNearestStore(this.stores, origin) : null;
      if (!store) {
        this.setBusy(false);
        this.showMessage(MESSAGES.notFound);
        return;
      }

      // De knop blijft in de laadstand staan: de navigatie is al ingezet, dus
      // een tweede zoekopdracht heeft geen zin meer.
      window.location.assign(store.pageUrl || FALLBACK_URL);
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.submit) return;

      this.submit.disabled = busy;
      this.submit.textContent = busy ? this.busyLabel : this.defaultLabel;
    }

    showMessage(text) {
      if (!this.message) return;

      this.message.textContent = text;
      this.message.hidden = false;
    }

    clearMessage() {
      if (!this.message || this.message.hidden) return;

      this.message.textContent = '';
      this.message.hidden = true;
    }
  }

  customElements.define('postcode-vestiging', PostcodeVestiging);
}
